import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Gift, Check } from '@phosphor-icons/react';
import { fetchProducts } from '../lib/api';
import { OFFER_TIERS, useCart } from '../lib/CartContext';

const FreePosterPicker = () => {
  const { pickerTier, closePicker, setReward } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const tier = useMemo(
    () => OFFER_TIERS.find((t) => t.id === pickerTier) || null,
    [pickerTier],
  );

  useEffect(() => {
    if (!tier) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchProducts();
        if (cancelled) return;
        const filtered = tier.filter
          ? data.filter(tier.filter)
          : data;
        setProducts(filtered);
        setSelectedId(null);
      } catch (err) {
        console.error('Failed to load reward candidates', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tier]);

  const handleConfirm = () => {
    const chosen = products.find((p) => p.id === selectedId);
    if (chosen && tier) {
      setReward(tier.id, chosen);
    }
  };

  return (
    <AnimatePresence>
      {tier && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md"
            onClick={closePicker}
            data-testid="picker-backdrop"
          />
          <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ type: 'spring', damping: 26, stiffness: 260 }}
              className="pointer-events-auto w-full max-w-3xl max-h-[85vh] flex flex-col bg-background border border-border/40 rounded-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={`Choose your free ${tier.label}`}
              data-testid="picker-modal"
            >
              <header className="flex items-start justify-between px-6 py-5 border-b border-border/40 flex-shrink-0">
                <div className="flex items-start gap-3">
                  <div className="text-3xl leading-none" aria-hidden>
                    {tier.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Gift size={16} weight="fill" className="text-white" />
                      <span className="text-xs font-bold tracking-widest text-white uppercase">
                        Reward Unlocked
                      </span>
                    </div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-foreground mt-1">
                      Pick your free {tier.label.replace('Free ', '')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">{tier.reward}</p>
                  </div>
                </div>
                <button
                  onClick={closePicker}
                  className="text-muted-foreground hover:text-foreground p-1 rounded-sm"
                  aria-label="Close picker"
                  data-testid="close-picker"
                >
                  <X size={22} weight="bold" />
                </button>
              </header>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4" data-lenis-prevent>
                {loading ? (
                  <p className="text-center text-muted-foreground py-16">Loading eligible posters…</p>
                ) : products.length === 0 ? (
                  <p className="text-center text-muted-foreground py-16">
                    No eligible posters available right now. Please check back soon.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="picker-grid">
                    {products.map((p) => {
                      const selected = selectedId === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setSelectedId(p.id)}
                          className={`relative group text-left overflow-hidden rounded-sm border-2 transition-all ${
                            selected
                              ? 'border-white ring-2 ring-white/50 shadow-lg'
                              : 'border-border/30 hover:border-border'
                          }`}
                          data-testid={`picker-option-${p.id}`}
                        >
                          <div
                            className="w-full bg-secondary"
                            style={{
                              aspectRatio:
                                p.category === 'Split Posters' ? '16 / 9' : '3 / 4',
                            }}
                          >
                            <img
                              src={p.image_url}
                              alt={p.name}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {selected && (
                            <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white text-black flex items-center justify-center">
                              <Check size={14} weight="bold" />
                            </div>
                          )}
                          <div className="p-2 bg-background">
                            <p className="text-xs font-medium text-foreground line-clamp-1">
                              {p.name}
                            </p>
                            <p className="text-[10px] text-muted-foreground">{p.category}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <footer className="flex-shrink-0 px-6 py-4 border-t border-border/40 flex items-center justify-between gap-3 bg-secondary/40">
                <p className="text-xs text-muted-foreground">
                  It&apos;ll be added to your cart for <span className="font-bold text-white">₹0</span>.
                </p>
                <button
                  onClick={handleConfirm}
                  disabled={!selectedId}
                  className="bg-white text-black py-2.5 px-6 rounded-sm font-heading font-bold text-sm inline-flex items-center gap-2 hover:bg-white/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="confirm-picker"
                >
                  <Gift size={16} weight="fill" />
                  Claim Reward
                </button>
              </footer>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FreePosterPicker;
